import { useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '@tanstack/react-query';
import { DepartmentSchema } from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { getData, getPage } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import {
  DEPARTMENTS,
  DepartmentRef,
  MasterDialog,
  MasterRowSchema,
  MasterStatusDialog,
  type MasterKind,
} from './MastersPage';
import { MigrationTrace } from '../migration/MigrationTrace';

/**
 * One department, cost centre or location at its own address (FRD-CORE-002): its facts, the
 * department hierarchy around it (FRD-CORE-004), and — where the session holds the change
 * permission — Edit and a confirmed Activate / Deactivate (FRD-CORE-007), with its audit history
 * (FRD-CORE-030). An unknown id, or one the session may not see, reads as "no such record".
 */
export function MasterDetailPage({ kind }: { kind: MasterKind }) {
  const { id = '' } = useParams();
  const session = useSession();
  const canChange = hasCapability(session.data, kind.changeCapability);
  const [dialog, setDialog] = useState<'edit' | 'status' | null>(null);
  const record = useQuery({
    queryKey: [kind.queryKey, 'one', id],
    queryFn: () => getData(`${kind.path}/${id}`, MasterRowSchema),
  });
  const parentId = record.data?.parentDepartmentId ?? null;
  const parent = useQuery({
    queryKey: ['departments', 'one', parentId],
    enabled: kind.hierarchical && parentId !== null,
    queryFn: () => getData(`${DEPARTMENTS.path}/${parentId ?? ''}`, DepartmentSchema),
  });
  const back = (
    <Button component={RouterLink} to={kind.route} startIcon={<ArrowBackIcon />}>
      All {kind.title.toLowerCase()}
    </Button>
  );
  const title = kind.singular.charAt(0).toUpperCase() + kind.singular.slice(1);
  if (record.isPending) return <LoadingState label={`Loading the ${kind.singular}`} />;
  if (record.isError) {
    return <RecordFailure title={title} noun={kind.singular} error={record.error} back={back} />;
  }
  const row = record.data;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${row.code} — ${row.name}`}
        description={title}
        actions={
          <>
            {back}
            {canChange && (
              <>
                <Button
                  variant="outlined"
                  color={row.status === 'active' ? 'warning' : 'primary'}
                  onClick={(event) => {
                    event.currentTarget.focus();
                    setDialog('status');
                  }}
                >
                  {row.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="contained"
                  onClick={(event) => {
                    event.currentTarget.focus();
                    setDialog('edit');
                  }}
                >
                  Edit
                </Button>
              </>
            )}
          </>
        }
      />
      <Section title={title}>
        <Facts
          label={`${title} details`}
          facts={[
            ['Code', row.code],
            ['Name', row.name],
            ['Status', <StatusChip key="s" status={row.status} />],
            ...(kind.hierarchical
              ? ([
                  [
                    'Parent department',
                    parentId === null ? (
                      'None (top level)'
                    ) : (
                      <DepartmentRef key="p" id={parentId} />
                    ),
                  ],
                ] as [string, ReactNode][])
              : []),
          ]}
        />
      </Section>
      {kind.hierarchical && <ChildDepartments departmentId={row.id} />}
      <MigrationTrace recordType={kind.recordType} recordId={row.id} />
      <RecordHistory recordType={kind.recordType} recordId={row.id} />
      {dialog === 'edit' && (
        <MasterDialog
          key={String(row.version)}
          kind={kind}
          row={row}
          parent={parent.data ?? null}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'status' && (
        <MasterStatusDialog
          kind={kind}
          row={row}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </Stack>
  );
}

/** The departments directly below this one, read through the same list and permission. */
function ChildDepartments({ departmentId }: { departmentId: string }) {
  const children = useQuery({
    queryKey: ['departments', 'children', departmentId],
    queryFn: () =>
      getPage(
        `${DEPARTMENTS.path}?pageSize=100&sort=code&filter[parentDepartmentId]=${departmentId}`,
        DepartmentSchema,
      ),
  });
  return (
    <Section title="Sub-departments">
      {children.isPending && <LoadingState label="Loading sub-departments" />}
      {children.isError && <ErrorState message={children.error.message} />}
      {children.data?.items.length === 0 && <EmptyState title="No sub-departments" />}
      {children.data !== undefined && children.data.items.length > 0 && (
        <Table size="small" aria-label="Sub-departments">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {children.data.items.map((child) => (
              <TableRow key={child.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <RecordLink to={`${DEPARTMENTS.route}/${child.id}`}>{child.code}</RecordLink>
                </TableCell>
                <TableCell>{child.name}</TableCell>
                <TableCell>
                  <StatusChip status={child.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}
